<template>
  <div class="form-group">
    <label
      v-if="hasLabel"
      :class="labelClass"
      :for="cmpId"
    >
      {{ label }}
      <slot/>
    </label>

    <multiselect
      :id="cmpId"
      ref="multi"
      :allow-empty="allowEmpty"
      :block-keys="blockKeys"
      :class="multiselectClass"
      :clear-on-select="clearOnSelect"
      :close-on-select="closeOnSelect"
      :custom-label="customLabel"
      :deselect-group-label="deselectGroupLabel"
      :deselect-label="deselectLabel"
      :disabled="disabled"
      :group-label="groupLabel"
      :group-select="groupSelect"
      :group-values="groupValues"
      :hide-selected="hideSelected"
      :highlighted="highlighted"
      :internal-search="internalSearch"
      :label="itemLabel"
      :limit="limit"
      :limit-text="limitText"
      :loading="loading"
      :max="max"
      :max-height="maxHeight"
      :model-value="modelValue"
      :multiple="multiple"
      :name="name"
      :open-direction="openDirection"
      :option-height="optionHeight"
      :options="options"
      :options-limit="optionsLimit"
      :placeholder="placeholder"
      :preselect-first="preselectFirst"
      :preserve-search="preserveSearch"
      :reset-after="resetAfter"
      :searchable="searchable"
      :select-label="selectLabel"
      :selected-label="selectedLabel"
      :show-labels="showLabels"
      :show-no-options="showNoOptions"
      :show-no-results="showNoResults"
      :show-pointer="showPointer"
      :tabindex="tabindex"
      :tag-placeholder="tagPlaceholder"
      :tag-position="tagPosition"
      :taggable="taggable"
      :track-by="trackBy"
      @close="onClose"
      @mouseleave="toggleHovered(false)"
      @mouseover="toggleHovered(true)"
      @open="onOpen"
      @search-change="onSearchChange"
      @select="onSelect"
      @update:model-value="onInput"
    >
      <template #caret="{ toggle }">
        <div class="multiselect__caret-wrapper">
          <!--
            @slot Element for opening and closing the dropdown.
            @binding {Function} toggle toggles the dropdown.
          -->
          <slot
            name="dropdownCaret"
            :toggle="toggle"
          >
            <span
              class="multiselect__caret"
              @mousedown.prevent.stop="toggle"
            />
          </slot>
        </div>
      </template>

      <template
        v-if="hasSingleLabel"
        #singleLabel="{ option }"
      >
        <!--
          @slot Slot for custom label template for single select.
          @binding option option
        -->
        <slot
          name="singleLabel"
          :option="option"
        />
      </template>

      <template
        v-if="hasOption"
        #option="{ option, search }"
      >
        <!--
          @slot Slot for custom option template. By default shows option item label
          @binding option option
          @binding search current search value
        -->
        <slot
          name="option"
          :option="option"
          :search="search"
        />
      </template>

      <template
        v-if="hasNoResult"
        #noResult
      >
        <!--
          @slot Shows when no elements match the search query.
          Default: "No elements found." Consider changing the search query.
        -->
        <slot name="noResultMessage"/>
      </template>

      <template
        v-if="hasNoOptions"
        #noOptions
      >
        <!--
          @slot Shows when no elements in options empty. Default: "List is empty."
        -->
        <slot name="noOptionsMessage"/>
      </template>

      <template
        v-if="hasTag"
        #tag="{ option, search, remove }"
      >
        <!--
          @slot Slot that is used for all selected options (tags)
          @binding option selected option
          @binding search the search value
          @binding remove method to remove the slot (pass the option)
        -->
        <slot
          name="tag"
          :option="option"
          :remove="remove"
          :search="search"
        />
      </template>
    </multiselect>

    <div
      v-if="helpBlock"
      :id="ariaDescribedBy"
      class="form-text small"
    >
      {{ helpText }}
    </div>

    <!--
      @slot Space for overriding custom validation feedback
    -->
    <slot name="validationFeedback">
      <div
        v-if="validationFeedbackText"
        :class="validationFeedbackClass"
      >
        {{ validationFeedbackText }}
      </div>
    </slot>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';

import { SIZE_MD } from '../../config/constants';
import formControl from '../../mixins/form-control';
import formControlText from '../../mixins/form-control-text';
import { MULTISELECT_DIRECTIONS, MULTISELECT_SIZES } from './config';

function isEmpty(opt) {
  if (opt === 0) return false;
  if (Array.isArray(opt) && opt.length === 0) return true;

  return !opt;
}

export default {
  name: 'DpMultiselect',

  components: {
    Multiselect,
  },

  mixins: [
    formControl,
    formControlText,
  ],

  props: {
    id: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Allows to remove all selected values. Otherwise one must be left selected.
     * */
    allowEmpty: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * @values Array of strings
     * */
    blockKeys: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
    clearOnSelect: {
      type: Boolean,
      required: false,
      default: true,
    },
    closeOnSelect: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Function to interpolate the custom label
     * */
    customLabel: {
      type: Function,
      required: false,
      default(option, label) {
        if (isEmpty(option)) return '';

        return label ? option[label] : option;
      },
    },
    deselectGroupLabel: {
      type: String,
      required: false,
      default: 'Press enter to deselect group',
    },
    deselectLabel: {
      type: String,
      required: false,
      default: 'Press enter to remove',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    groupValues: {
      type: String,
      required: false,
      default: '',
    },
    groupLabel: {
      type: String,
      required: false,
      default: '',
    },
    groupSelect: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideSelected: {
      type: Boolean,
      required: false,
      default: false,
    },
    highlighted: {
      type: Boolean,
      required: false,
      default: false,
    },
    internalSearch: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * This prop is mapped to vue-multiselect `label` prop
     */
    itemLabel: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * @values 0...99999
     * */
    limit: {
      type: Number,
      required: false,
      default: 99999,
    },
    /**
     * Function that process the message shown when selected
     * */
    limitText: {
      type: Function,
      required: false,
      default: (count) => `and ${count} more`,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    max: {
      type: [Number, Boolean],
      required: false,
      default: false,
    },
    /**
     * @values 0...99999
     * */
    maxHeight: {
      type: Number,
      required: false,
      default: 300,
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false,
    },
    name: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * @values above, below, bottom, top
     * */
    openDirection: {
      type: String,
      required: false,
      default: '',
      validator: (direction) => !direction || MULTISELECT_DIRECTIONS.includes(direction),
    },
    /**
     * @values Array of options
     * */
    options: {
      type: Array,
      required: true,
      default: null,
    },
    optionHeight: {
      type: Number,
      required: false,
      default: 44,
    },
    /**
     * @values 0...1000
     * */
    optionsLimit: {
      type: Number,
      required: false,
      default: 1000,
    },
    placeholder: {
      type: String,
      required: false,
      default: 'Select option',
    },
    preselectFirst: {
      type: Boolean,
      required: false,
      default: false,
    },
    preserveSearch: {
      type: Boolean,
      required: false,
      default: false,
    },
    resetAfter: {
      type: Boolean,
      required: false,
      default: false,
    },
    searchable: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectLabel: {
      type: String,
      required: false,
      default: 'Press enter to select',
    },
    selectedLabel: {
      type: String,
      required: false,
      default: 'Selected',
    },
    showLabels: {
      type: Boolean,
      required: false,
      default: false,
    },
    showNoOptions: {
      type: Boolean,
      required: false,
      default: true,
    },
    showNoResults: {
      type: Boolean,
      required: false,
      default: false,
    },
    showPointer: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * @values md, lg
     * @default md
     * */
    size: {
      required: false,
      type: String,
      default: SIZE_MD,
      validator: (value) => MULTISELECT_SIZES.includes(value),
    },
    /**
     * @values 0...99999
     * */
    tabindex: {
      type: Number,
      required: false,
      default: 0,
    },
    tagPlaceholder: {
      type: String,
      required: false,
      default: 'Press enter to create a tag',
    },
    tagPosition: {
      type: String,
      required: false,
      default: 'top',
    },
    taggable: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Name of key to be tracked by the dropdown
     * */
    trackBy: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Selected value - [Object, String]
     * */
    modelValue: {
      type: [Object, String, Array],
      required: false,
      default: () => [],
    },

    fieldLabel: {
      required: false,
      type: String,
      default: null,
    },

    hideLabel: {
      type: Boolean,
      default: false,
      required: false,
    },

    optionTextEllipsis: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  emits: [
    'update:modelValue',
    'open',
    'close',
    'select',
    'search-change',
  ],

  data: () => ({
    isActive: false,
    isHovered: false,
    isHighlighted: false,
  }),

  computed: {
    hasNoResult() {
      return !!this.$slots.noResultMessage;
    },

    hasNoOptions() {
      return !!this.$slots.noOptionsMessage;
    },

    hasOption() {
      return !!this.$slots.option;
    },

    hasTag() {
      return !!this.$slots.tag;
    },

    hasSingleLabel() {
      return !!this.$slots.singleLabel;
    },

    multiselectClass() {
      const { size, isHighlighted, validationClass, optionTextEllipsis } = this;
      const classes = [
        validationClass,
        { 'dp-multiselect': true },
        { 'multiselect--highlighted': isHighlighted },
        { 'multiselect--option-text-ellipsis': optionTextEllipsis },
      ];

      if (size) {
        classes.push(`multiselect-${size}`);
      }

      return classes;
    },
  },

  watch: {
    isActive: {
      handler() {
        this.isHighlighted = false;
      },
    },
  },

  created() {
    this.isHighlighted = this.highlighted;
  },

  methods: {
    onInput(value, id) {
      /**
       * Emitted after input changes
       * @property {any} value Selected value
       * @property {any} id Component id
       * */
      this.$emit('update:modelValue', value, id);
    },

    onOpen(id) {
      /**
       * Emitted when the dropdown opens
       * @property {any} id
       * */
      this.$emit('open', id);
      this.toggleActive(true);
    },

    onClose(value, id) {
      /**
       * Emitted when the dropdown closes
       * @property {any} value Selected value
       * @property {any} id Component id
       * */
      this.$emit('close', value, id);
      this.toggleActive(false);
    },

    onSelect(selectedOption, id) {
      /**
       * Emitted after selecting an option
       * @property {any} selectedOption Selected value
       * @property {any} id Component id
       * */
      this.$emit('select', selectedOption, id);
    },

    onSearchChange(searchQuery, id) {
      /**
       * Emitted after the search query changes
       * @property {any} searchQuery Selected value
       * @property {any} id Component id
       * */
      this.$emit('search-change', searchQuery, id);
    },

    toggleActive(value) {
      this.isActive = value;
    },

    toggleHovered(value) {
      this.isHovered = value;
    },
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>

<!-- eslint-disable -->
<style lang="scss">
@use "../../../scss/src/bootstrap/variables" as *;
@import "../../../scss/src/bootstrap/mixins/text-truncate";

@mixin multiselect-validation($color) {
  .multiselect__content-wrapper {
    border: 1px solid $dropdown-border-color;
  }

  .multiselect__option {
    border-bottom: 1px solid $dropdown-border-color;
  }

  .multiselect__tags {
    border: 1px solid $color;
  }

  &.multiselect--active,
  &.multiselect--highlighted {
    .multiselect__tags {
      border-color: darken($color, 10%);
      box-shadow: inset 0 1px 1px rgba(0, 0, 0, .075), 0 0 6px lighten($color, 20%);
    }
  }
}

.dp-multiselect {
  cursor: pointer;
  font-size: $font-size-base;
  min-height: $input-height;

  .multiselect__content {
    width: 100%;
  }

  .multiselect__input,
  .multiselect__single,
  .multiselect__placeholder,
  .multiselect__input::placeholder {
    font-size: $input-font-size;
    line-height: $line-height-lg;
    margin: 0;
    overflow: hidden;
    padding: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .multiselect__placeholder {
    color: $input-placeholder-color;
  }

  .multiselect__content-wrapper {
    border: 1px solid $dropdown-border-color;
    box-shadow: $box-shadow;
  }

  .multiselect__option {
    border-bottom: 1px solid $dropdown-border-color;
    line-height: $line-height-base;
    padding: $spacer;
    white-space: normal;
  }

  .multiselect__option--highlight {
    background: $gray-200;
    color: $gray-900;

    &:after {
      background: $gray-200;
    }
  }

  .multiselect__option--selected {
    background: $primary-light;
    color: $gray-900;
    font-weight: normal;

    &:after {
      color: $gray-200;
    }
  }

  .multiselect__option--selected {
    &.multiselect__option--highlight {
      background: $gray-200;
      color: $gray-900;
    }
  }

  .multiselect__option--selected {
    &.multiselect__option--highlight:after {
      background: $gray-200;
      color: $gray-900;
      content: attr(data-deselect);
    }
  }

  &.multiselect--option-text-ellipsis {
      .multiselect__option {
        @include text-truncate;
      }
  }

  .multiselect__element {
    &:last-of-type {
      .multiselect__option {
        border-bottom: none;
      }
    }
  }

  .multiselect__tags {
    align-items: center;
    border: 1px solid $input-border-color;
    border-radius: $input-border-radius;
    display: flex;
    flex-wrap: wrap;
    min-height: $input-height;
    padding: $custom-select-padding-y ($custom-select-padding-x + $custom-select-indicator-padding) $custom-select-padding-y $custom-select-padding-x;
  }

  .multiselect__spinner {
    height: calc(100% - 2px);
  }

  .multiselect__tags-wrap {
    display: inline-block;
    line-height: 0;
  }

  .multiselect__strong {
    margin-bottom: 3px;
  }

  .multiselect__tag {
    background: $primary-light;
    color: $primary;
    font-weight: bold;
    margin: 2px 5px 2px 0;
  }

  .multiselect__tag-icon {
    color: $primary;

    &:after {
      color: $primary;
    }

    &:focus,
    &:hover {
      background: $primary;
    }

    &:focus:after,
    &:hover:after {
      color: $white;
    }
  }

  .multiselect--above {
    .multiselect__content-wrapper {
      border-color: $gray-200;
    }
  }

  &.multiselect--active,
  &.multiselect--highlighted {
    .multiselect__tags {
      border-color: $custom-select-focus-border-color;
      box-shadow: $custom-select-focus-box-shadow;
      outline: 0;
    }

    .multiselect__placeholder {
      display: inline-block;
    }
  }

  &.multiselect--disabled {
    .multiselect__tags {
      border-color: $gray-300;
    }
  }

  // Custom caret styles
  .multiselect__caret-wrapper {
    height: $input-height;
    padding: 10px;
    position: absolute;
    right: 0;
    top: calc(50% - (#{$input-height} * 0.5));
  }

  .multiselect__caret {
    background: $custom-select-bg $custom-select-background;
    background-position-x: center;
    display: block;
    height: 100%;
    transition: transform 0.2s ease;
    width: $custom-select-bg-size;
  }

  .multiselect--active {
    z-index: $zindex-dropdown;

    .multiselect__caret {
      transform: rotateZ(180deg);
    }
  }
}

.dp-multiselect.multiselect-lg {
  min-height: $input-height-lg;

  .multiselect__tags {
    min-height: $input-height-lg;
  }
}

.is-valid {
  @include multiselect-validation($success);
}

.is-invalid {
  @include multiselect-validation($danger);
}
</style>
